import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { User, ClipboardList, Wallet } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { getUnreadCount, NOTIFICATION_UNREAD_EVENT } from "../../services/notificationService";

export default function AccountSidebar() {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  const accountSubRoutes = useMemo(
    () => [
      "/account/profile",
      "/account/addresses",
      "/account/change-password",
      "/account/privacy",
      "/account/notifications",
      "/account/notification-settings",
    ],
    []
  );

  const isInAccountSubRoute = useMemo(
    () => accountSubRoutes.some((p) => location.pathname.startsWith(p)),
    [accountSubRoutes, location.pathname]
  );

  const [isAccountOpen, setIsAccountOpen] = useState(isInAccountSubRoute);

  useEffect(() => {
    if (isInAccountSubRoute) setIsAccountOpen(true);
  }, [isInAccountSubRoute]);

  useEffect(() => {
    let active = true;
    if (!isAuthenticated) {
      setUnreadNotifications(0);
      return undefined;
    }

    const refresh = () => {
      getUnreadCount()
        .then((count) => { if (active) setUnreadNotifications(Math.max(0, Number(count) || 0)); })
        .catch(() => {});
    };
    const handleUnreadUpdate = (event) => {
      if (active) setUnreadNotifications(Math.max(0, Number(event.detail?.count) || 0));
    };
    const handleVisibility = () => { if (document.visibilityState === "visible") refresh(); };

    refresh();
    window.addEventListener(NOTIFICATION_UNREAD_EVENT, handleUnreadUpdate);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      active = false;
      window.removeEventListener(NOTIFICATION_UNREAD_EVENT, handleUnreadUpdate);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [isAuthenticated]);

  const toggleAccount = () => setIsAccountOpen((prev) => !prev);

  const subLinkClass = ({ isActive }) => {
    const base =
      "block w-full rounded-xl px-3 py-2 text-left text-sm transition-all duration-200 " +
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 dark:focus-visible:ring-neutral-600";

    return isActive
      ? base +
          " font-semibold text-neutral-900 bg-neutral-100 border-2 border-neutral-300 dark:text-neutral-50 dark:bg-neutral-800 dark:border-neutral-600"
      : base +
          " border-2 border-transparent text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50 dark:text-neutral-400 dark:hover:text-neutral-200 dark:hover:bg-neutral-800";
  };

  const mainLinkBase =
    "flex items-center gap-2 px-3 py-2 font-bold rounded-xl transition-all duration-200 " +
    "focus:outline-none focus:ring-2 focus:ring-neutral-400 dark:focus:ring-neutral-600";

  const mainNavClass = ({ isActive }) =>
    mainLinkBase +
    (isActive
      ? " text-neutral-900 bg-neutral-100 dark:text-neutral-50 dark:bg-neutral-800"
      : " text-neutral-900 dark:text-neutral-100 hover:bg-neutral-50 dark:hover:bg-neutral-800");

  return (
    <aside
      className="
        w-full md:w-64 shrink-0
        rounded-2xl border p-4
        flex flex-col gap-4
        bg-white border-neutral-200
        dark:bg-neutral-900 dark:border-neutral-800
      "
    >
      {/* My Account */}
      <div className="flex flex-col gap-1">
        <button
          type="button"
          onClick={toggleAccount}
          className="
            flex items-center gap-2 px-3 py-2 text-left w-full rounded-xl
            transition-all duration-200
            hover:bg-neutral-50 dark:hover:bg-neutral-800
            text-neutral-900 dark:text-neutral-100 font-bold
            focus:outline-none focus:ring-2 focus:ring-neutral-400 dark:focus:ring-neutral-600
          "
        >
          <User className="w-5 h-5 shrink-0" />
          <span className="flex-1">My Account</span>
        </button>

        {/* Animated Collapse */}
        <div
          className={`
            grid transition-all duration-300 ease-in-out
            ${isAccountOpen ? "grid-rows-[1fr] opacity-100 mt-1" : "grid-rows-[0fr] opacity-0"}
          `}
        >
          <div className="overflow-hidden">
            <div
              className={`
                ml-4 pl-3 pr-1 py-1
                border-l border-neutral-200 dark:border-neutral-700
                flex flex-col gap-1
                transform transition-all duration-300
                ${isAccountOpen ? "translate-y-0" : "-translate-y-2"}
              `}
            >
              <NavLink to="/account/profile" className={subLinkClass}>
                Profile
              </NavLink>

              <NavLink to="/account/addresses" className={subLinkClass}>
                Addresses
              </NavLink>

              <NavLink to="/account/change-password" className={subLinkClass}>
                Change Password
              </NavLink>

              <NavLink to="/account/privacy" className={subLinkClass}>
                Privacy Settings
              </NavLink>

              <NavLink to="/account/notifications" className={subLinkClass}>
                <span className="flex items-center justify-between gap-2">
                  <span>Notifications</span>
                  {unreadNotifications > 0 ? (
                    <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold leading-4 text-white" aria-label={`${unreadNotifications} unread notifications`}>
                      {unreadNotifications > 99 ? "99+" : unreadNotifications}
                    </span>
                  ) : null}
                </span>
              </NavLink>

              <NavLink to="/account/notification-settings" className={subLinkClass}>
                Notification Settings
              </NavLink>
            </div>
          </div>
        </div>
      </div>

      {/* My Purchase (ONLY this collapses My Account) */}
      <div className="flex flex-col gap-2">
        <NavLink
          to="/account/wallet"
          className={mainNavClass}
          onClick={() => setIsAccountOpen(false)}
        >
          <Wallet className="w-5 h-5 shrink-0" />
          <span>Shopper Credit</span>
        </NavLink>

        <NavLink
          to="/account/purchase"
          className={mainNavClass}
          onClick={() => setIsAccountOpen(false)}
        >
          <ClipboardList className="w-5 h-5 shrink-0" />
          <span>My Purchase</span>
        </NavLink>
      </div>
    </aside>
  );
}
